import { BACKEND_URI } from '@/config/env'
import axios, { Axios, AxiosError, AxiosRequestConfig } from 'axios'
import { logout } from '../api/axiosAuthorization'
import { randomString } from './randomString'
import {
  checkTokenExpiration,
  shouldSkipCheck,
  shouldThrottle,
  handleExpiredToken,
} from './tokenExpirationCheck'
import { authDebug } from './authDebug'

// axios 인스턴스 생성
const client: Axios = axios.create({
  baseURL: BACKEND_URI,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
})
client.interceptors.request.use(async (config) => {
  config.headers.randomValue = randomString()

  const skip = shouldSkipCheck(config.url)
  const throttled = shouldThrottle()
  authDebug.requestInterceptor(config.method || 'unknown', config.url || '', skip, throttled)

  if (!skip && !throttled) {
    const isValid = await checkTokenExpiration()
    if (!isValid) {
      handleExpiredToken()
      return Promise.reject(new axios.Cancel('Token expired'))
    }
  }

  return config
})
// Response wrapper type from backend
interface ApiResponse<T> {
  success: number
  data: T
}

client.interceptors.response.use(
  (res) => {
    // Unwrap response if it has {success, data} format
    if (res.data && typeof res.data === 'object' && 'success' in res.data && 'data' in res.data) {
      const wrapped = res.data as ApiResponse<unknown>
      if (wrapped.success === 1) {
        res.data = wrapped.data
      }
    }
    return res
  },
  async (err: AxiosError) => {
    const status = err.response?.status
    const errorCode = (err.response?.data as { code?: string })?.code

    if (status === 401 || (status === 400 && errorCode === 'UNAUTHORIZED')) {
      authDebug.unauthorized(err.config?.url || 'unknown')
      logout()
      return Promise.reject(err)
    }
    return Promise.reject(err)
  },
)

function checkValidateStatus(status: number) {
  return status >= 200 && status < 400
}

export const getData = async <T>(url: string, config?: AxiosRequestConfig) => {
  if (config === undefined) config = { validateStatus: checkValidateStatus }
  else config.validateStatus = checkValidateStatus
  const response = await client.get<T>(url, config)
  return response.data
}

export const postData = async <T>(url: string, data?: unknown, config?: AxiosRequestConfig) => {
  if (config === undefined) config = { validateStatus: checkValidateStatus }
  else config.validateStatus = checkValidateStatus
  const response = await client.post<T>(url, data, config)
  return response.data
}

export const putData = async <T>(url: string, data?: unknown, config?: AxiosRequestConfig) => {
  if (config === undefined) config = { validateStatus: checkValidateStatus }
  else config.validateStatus = checkValidateStatus
  const response = await client.put<T>(url, data, config)
  return response.data
}

export const patchData = async <T>(url: string, data?: unknown, config?: AxiosRequestConfig) => {
  if (config === undefined) config = { validateStatus: checkValidateStatus }
  else config.validateStatus = checkValidateStatus
  const response = await client.patch<T>(url, data, config)
  return response.data
}
